/**
 * The manual — OpenComply's knowledge base.
 *
 * WHAT THIS IS
 * A built-in handbook: one reference article per section of the product, plus
 * an ordered "getting started" path that says what to do in what order. It is
 * the long-form companion to the Help assistant (components/Help.tsx): the Help
 * panel answers "what next, right now" from your live records; the manual
 * explains what each screen is for and how the pieces fit together.
 *
 * WHY THE CONTENT LIVES HERE, IN CODE
 * The product owner's decision: the manual is part of the application, not data
 * in the database. It ships with the build, it is versioned with the code, and
 * — by rule — every new feature updates it in the same change that ships the
 * feature (see docs/CONTRIBUTING-KB.md). It is English-only for now, by the
 * same decision, so unlike the translated UI chrome this is plain text, not an
 * i18n string table.
 *
 * HOW IT IS CONSUMED
 *   - pages/Manual.tsx renders the index (the getting-started path + every
 *     article, grouped) and a single-article reader.
 *   - components/Help.tsx deep-links the "?" on each screen to the article for
 *     that screen, via `manualSlugForRoute`.
 *
 * TO ADD OR CHANGE AN ARTICLE
 * Edit MANUAL_ARTICLES below. Keep the prose plain and factual — this is a
 * manual, not marketing. Give every article at least a `summary` and a
 * `purpose`; add `steps` when the screen has an order to work through, `tips`
 * for the easy-to-miss things, and `related` for where to go next. List the
 * route(s) the article documents in `routes` so the on-screen "?" can find it.
 */

export interface ManualStep {
  title: string;
  body: string;
}

export interface ManualLink {
  /** An in-app route ("/gaps") or another article slug ("gaps"). */
  to: string;
  label: string;
}

export interface ManualArticle {
  slug: string;
  title: string;
  /** Group id — see MANUAL_GROUPS for the order and headings. */
  group: string;
  /** One sentence, shown on the index card and at the top of the article. */
  summary: string;
  /** A short paragraph: what this screen is for and when you use it. */
  purpose: string;
  /** Ordered things to do here. Omit for screens with no inherent order. */
  steps?: ManualStep[];
  /** Easy-to-miss points, cautions, good practice. */
  tips?: string[];
  /** Where to go next. `to` may be a route or another article slug. */
  related?: ManualLink[];
  /**
   * The in-app route(s) this article documents, most-specific first. Used to
   * resolve the on-screen "?" to an article. A trailing "/*" matches a prefix.
   */
  routes: string[];
}

export interface ManualGroup {
  id: string;
  title: string;
  blurb: string;
}

/** The sections of the manual, in reading order. */
export const MANUAL_GROUPS: ManualGroup[] = [
  { id: 'start', title: 'Getting started', blurb: 'Find your feet and set your goal.' },
  { id: 'assess', title: 'Assess and close gaps', blurb: 'The core loop: licence a framework, answer it, fix what it finds.' },
  { id: 'risk', title: 'Risk and controls', blurb: 'The artefacts an auditor expects, and how to prove one thing once.' },
  { id: 'operate', title: 'Run the programme', blurb: 'Policies, incidents and the deadlines that keep coming.' },
  { id: 'thirdparty', title: 'Third parties', blurb: 'Your suppliers, and the people who ask about you.' },
  { id: 'eu', title: 'EU product rules', blurb: 'Conformity for the products you place on the EU market.' },
  { id: 'report', title: 'Show your standing', blurb: 'The board report and the numbers behind it.' },
  { id: 'admin', title: 'Administration', blurb: 'Scans, the audit trail, languages, people and credits.' },
];

export const MANUAL_ARTICLES: ManualArticle[] = [
  // --- Getting started -----------------------------------------------------
  {
    slug: 'overview',
    title: 'What OpenComply is',
    group: 'start',
    summary: 'A single place to meet the EU cybersecurity rules that apply to you, and to prove it.',
    purpose:
      'OpenComply turns a regulation into a questionnaire, shows you where you fall short, helps you ' +
      'plan the fixes, and keeps the evidence and the paperwork an auditor or a customer will ask for. ' +
      'It covers NIS2, DORA, GDPR, the EU AI Act, the Cyber Resilience Act and the Cybersecurity Act, ' +
      'alongside ISO 27001, NIST CSF and CIS Controls.',
    steps: [
      { title: 'Set your goal', body: 'Tell OpenComply what you are working towards. Everything it suggests next is chosen from that goal and your real records.' },
      { title: 'Follow the checklist', body: 'The Getting started page turns your goal into an ordered checklist. Work down it, or let the Guide me button point you at the single next step.' },
      { title: 'Come back to the manual', body: 'Each screen has a "?" that opens its article here. You do not need to read this end to end — reach for it when a screen is unfamiliar.' },
    ],
    tips: [
      'The Guide me button (bottom of the sidebar) is the fastest way to get un-stuck: it always names one next step.',
      'You can change your goal at any time from the Getting started page; the checklist re-orders itself.',
    ],
    related: [
      { to: '/getting-started', label: 'Open Getting started' },
      { to: 'frameworks', label: 'Next: licence a framework' },
    ],
    routes: [],
  },
  {
    slug: 'getting-started',
    title: 'Getting started and your goal',
    group: 'start',
    summary: 'Set what you are working towards and get an ordered checklist to reach it.',
    purpose:
      'This is where you choose a goal — the framework or outcome you are aiming for — and see the full ' +
      'checklist derived from it. It is reachable forever, not just on your first day, because the people ' +
      'most lost in a compliance tool are often the ones who have been using it for months.',
    steps: [
      { title: 'Answer the goal question', body: 'Pick the framework or outcome you are driving towards. This is the one input the rest of the guidance is built on.' },
      { title: 'Work the checklist', body: 'Each item links straight to the screen that completes it. Items light up as done when your records show the work is done — you do not tick them by hand.' },
      { title: 'Restore anything you hid', body: 'If you dismissed a prompt on the dashboard, this page gives it back.' },
    ],
    related: [
      { to: '/', label: 'Back to the dashboard' },
      { to: 'assessments', label: 'Start your first assessment' },
    ],
    routes: ['/getting-started'],
  },
  {
    slug: 'dashboard',
    title: 'The dashboard',
    group: 'start',
    summary: 'Your organisation at a glance — standing, open work, and what needs attention.',
    purpose:
      'The home screen. It shows your overall compliance, what has changed lately, and the single next ' +
      'action worth taking. In daylight it leads with one clear score; after dark it switches to a denser ' +
      'view of the same numbers.',
    tips: [
      'The getting-started card here carries your next step; open the full checklist from it when you want the whole picture.',
      'Switch light and dark (and your language) from the bottom of the sidebar — the dashboard adapts to each.',
    ],
    related: [
      { to: '/insights', label: 'See the trend over time' },
      { to: 'reports', label: 'Generate the board report' },
    ],
    routes: ['/'],
  },

  // --- Assess and close gaps ----------------------------------------------
  {
    slug: 'frameworks',
    title: 'Frameworks',
    group: 'assess',
    summary: 'The regulations and standards you can work against. Licensing one unlocks its assessment.',
    purpose:
      'A framework is a body of rules — NIS2, DORA, ISO 27001 and the rest. Review what each requires, then ' +
      'licence the one you need: licensing opens its questionnaire and adds its requirements to your ' +
      'coverage and your Statement of Applicability.',
    steps: [
      { title: 'Review what applies', body: 'Open a framework to see what it covers before you commit to it.' },
      { title: 'Licence it', body: 'Licensing unlocks the assessment. If your plan charges credits for it, you will see the cost first.' },
      { title: 'Go to the assessment', body: 'Once licensed, start answering — that is where the real work happens.' },
    ],
    related: [
      { to: 'assessments', label: 'Next: answer the assessment' },
      { to: 'catalogue', label: 'See how controls span frameworks' },
    ],
    routes: ['/frameworks', '/frameworks/*'],
  },
  {
    slug: 'assessments',
    title: 'Self-assessments',
    group: 'assess',
    summary: 'Answer a framework question by question; OpenComply turns the shortfalls into gaps.',
    purpose:
      'An assessment is the framework as a set of plain-language questions. You record where you stand on ' +
      'each, attach evidence, and finalise. Every answer that falls short becomes a gap you can plan and ' +
      'fix. This is the heart of the product.',
    steps: [
      { title: 'Work through the questions', body: 'Each question shows the essentials first. Tap a question to unfold its guidance, the evidence it expects and the controls it maps to.' },
      { title: 'Save as you go', body: 'You can change your answers freely before you finalise — nothing is locked while the assessment is in progress.' },
      { title: 'Finalise', body: 'Finalising records your position and creates the gaps. From here, further edits are behind a warning.' },
      { title: 'Reopen only if you must', body: 'You can reopen a finalised assessment to correct an answer, but the better habit is usually a fresh assessment. When you re-finalise, gaps for answers you have put right are closed for you.' },
    ],
    tips: [
      'Switch the app to Greek and the questions themselves read in Greek, not just the menus — where a translation exists.',
      'Attaching evidence as you answer saves hunting for it later when an auditor asks.',
    ],
    related: [
      { to: 'gaps', label: 'Next: work your gaps' },
      { to: 'frameworks', label: 'Licence another framework' },
    ],
    routes: ['/assessments/*', '/assessments'],
  },
  {
    slug: 'gaps',
    title: 'Gaps',
    group: 'assess',
    summary: 'Everywhere a finalised assessment found you short, gathered in one list to act on.',
    purpose:
      'A gap is a specific shortfall an assessment turned up. This is your to-do list of what the rules ' +
      'expect but your answers said you do not yet have. Turn a gap into a remediation plan, or close it ' +
      'when the underlying answer changes.',
    steps: [
      { title: 'Triage the list', body: 'Read what each gap is and which requirement it comes from.' },
      { title: 'Plan the fix', body: 'Create a remediation plan from a gap to give it an owner and a date.' },
      { title: 'Close it honestly', body: 'A gap closes when the work is done and the assessment answer reflects it — not because you want the number to look better.' },
    ],
    tips: [
      'The screen opens as a visual board of gap cards. Switch to the grouped-by-section list from the control at the top right; your choice is remembered.',
      'Select several cards and create one remediation plan for all of them at once.',
    ],
    related: [
      { to: 'plans', label: 'Next: remediation plans' },
      { to: 'risk-register', label: 'Link gaps to risks' },
    ],
    routes: ['/gaps'],
  },
  {
    slug: 'plans',
    title: 'Remediation plans',
    group: 'assess',
    summary: 'The fixes for your gaps, each with an owner and a due date.',
    purpose:
      'A remediation plan is how a gap gets closed: what you will do, who owns it, and by when. Plans are ' +
      'where compliance stops being a snapshot and becomes work that moves.',
    steps: [
      { title: 'Create from a gap', body: 'Most plans start from a gap, so the link back to the requirement is kept.' },
      { title: 'Assign and schedule', body: 'Give it an owner and a target date so it shows up in the right people’s view and on the calendar.' },
      { title: 'Track to done', body: 'Update progress as the work happens; the dashboard and reports reflect it.' },
    ],
    related: [
      { to: 'gaps', label: 'Back to gaps' },
      { to: '/calendar', label: 'See due dates on the calendar' },
    ],
    routes: ['/plans'],
  },

  // --- Risk and controls ---------------------------------------------------
  {
    slug: 'risk-register',
    title: 'Risk register',
    group: 'risk',
    summary: 'Identify, score and treat information-security risks the way ISO, NIS2 and DORA expect.',
    purpose:
      'A risk register is a mandatory artefact across several frameworks. Each risk gets a likelihood and an ' +
      'impact, a treatment and the residual risk it leaves, an owner and a review date. A five-by-five heat ' +
      'map shows where your worst exposures sit.',
    steps: [
      { title: 'Record the risk', body: 'Describe it plainly and score its likelihood against its impact. The heat map places it for you.' },
      { title: 'Decide the treatment', body: 'Choose to treat, tolerate, transfer or terminate, and record the residual risk once the treatment is in place.' },
      { title: 'Link it to your work', body: 'Connect a risk to the gaps and controls that address it, so the register is not an island.' },
      { title: 'Give it an owner and a review date', body: 'A risk with no owner and no next review is a risk nobody is watching.' },
    ],
    related: [
      { to: 'gaps', label: 'Link gaps to risks' },
      { to: 'catalogue', label: 'See the controls that treat risks' },
    ],
    routes: ['/risk-register'],
  },
  {
    slug: 'soa',
    title: 'Statement of Applicability',
    group: 'risk',
    summary: 'Which controls apply to you, and why — the central document of an ISO 27001 audit.',
    purpose:
      'The Statement of Applicability lists every control in scope, says whether it applies, and gives the ' +
      'reason. It is the document an ISO 27001 auditor turns to first. OpenComply builds it from your ' +
      'licensed frameworks and your assessment answers.',
    tips: [
      'Keep the justifications current — an auditor reads the reasons, not just the yes/no.',
      'Export it from the Reports screen when you need a copy to hand over.',
    ],
    related: [
      { to: 'reports', label: 'Export it from Reports' },
      { to: 'catalogue', label: 'See the full control library' },
    ],
    routes: ['/soa'],
  },
  {
    slug: 'catalogue',
    title: 'Control catalogue — prove once, satisfy many',
    group: 'risk',
    summary: 'One control library across every framework, so a single piece of evidence counts many times.',
    purpose:
      'The catalogue is the cross-framework control library. Because one control often satisfies ' +
      'requirements in several frameworks at once, it shows you how far the controls you already have carry ' +
      'you everywhere — and which control to do next because it moves the most frameworks.',
    steps: [
      { title: 'See your coverage', body: 'For each control, see which frameworks it covers and whether you already hold the evidence.' },
      { title: 'Prove it once', body: 'Record the evidence for a control one time and it is reused everywhere that control applies.' },
      { title: 'Pick the highest-leverage next control', body: 'The coverage view ranks what to do next by how many frameworks it advances.' },
    ],
    related: [
      { to: 'frameworks', label: 'Frameworks' },
      { to: 'soa', label: 'Statement of Applicability' },
    ],
    routes: ['/catalogue/*', '/catalogue'],
  },

  // --- Run the programme ---------------------------------------------------
  {
    slug: 'policies',
    title: 'Policies',
    group: 'operate',
    summary: 'The written policies your controls depend on, kept and versioned here.',
    purpose:
      'Many controls require a written policy to back them. The policies screen is your library: draft, ' +
      'edit, and keep the same policy in more than one language as linked versions you can read side by ' +
      'side to check they match.',
    steps: [
      { title: 'Start from a template or blank', body: 'Create a policy from a built-in or your own template, or from scratch.' },
      { title: 'Edit and approve', body: 'Work in the editor; keep the policy current as your controls change.' },
      { title: 'Add a language', body: 'Add a linked translation of a policy straight from the policy itself, and compare the two.' },
    ],
    related: [
      { to: 'policy-templates', label: 'Manage your own templates' },
      { to: 'catalogue', label: 'See which controls need a policy' },
    ],
    routes: ['/policies/*', '/policies'],
  },
  {
    slug: 'policy-templates',
    title: 'Policy templates',
    group: 'operate',
    summary: 'Your organisation’s own reusable policy templates, alongside the built-in ones.',
    purpose:
      'Templates let you start a policy from a known-good baseline. Here you create, edit and retire ' +
      'templates of your own; they appear next to the system templates every time someone starts a new ' +
      'policy.',
    related: [
      { to: 'policies', label: 'Back to policies' },
    ],
    routes: ['/policies/templates'],
  },
  {
    slug: 'incidents',
    title: 'Incidents',
    group: 'operate',
    summary: 'Record security incidents and meet the reporting clocks the law sets.',
    purpose:
      'When something happens, this is where you log it and track it to resolution. For NIS2, OpenComply ' +
      'tracks the reporting deadlines — the early warning and the follow-up report — so a legal clock does ' +
      'not quietly run out.',
    steps: [
      { title: 'Open an incident', body: 'Record what happened, when, and its severity.' },
      { title: 'Watch the clocks', body: 'The NIS2 reporting deadlines are shown and counted down; the compliance calendar surfaces them too.' },
      { title: 'Close with a record', body: 'Keep the resolution and the lessons — this is the evidence that your process works.' },
    ],
    related: [
      { to: '/calendar', label: 'See incident deadlines on the calendar' },
    ],
    routes: ['/incidents/*', '/incidents'],
  },
  {
    slug: 'calendar',
    title: 'Compliance calendar',
    group: 'operate',
    summary: 'Every deadline across your programme in one place, grouped by how soon it falls.',
    purpose:
      'One calendar for everything that comes due: licence and evidence expiry, policy and assessment ' +
      'reviews, supplier questionnaires, and the NIS2 incident-reporting clocks. It is how you stop being ' +
      'surprised by a date.',
    related: [
      { to: 'plans', label: 'Plan due dates' },
      { to: 'incidents', label: 'Incident clocks' },
    ],
    routes: ['/calendar'],
  },

  // --- Third parties -------------------------------------------------------
  {
    slug: 'vendors',
    title: 'Vendors',
    group: 'thirdparty',
    summary: 'Your suppliers and their risk — the register behind third-party risk management.',
    purpose:
      'The vendor register is the list of the suppliers you depend on. Add them one by one or import a CSV, ' +
      'then send each a security questionnaire and watch where they stand on a single posture view.',
    steps: [
      { title: 'Add your suppliers', body: 'Enter them by hand, or upload a CSV of name, domain and contact. Duplicate or incomplete rows are skipped and reported.' },
      { title: 'Send a questionnaire', body: 'Pick the short snapshot or the longer NIS2/DORA assessment; the supplier answers from a personal link with no account to create.' },
      { title: 'Read the posture', body: 'Each supplier lands in a band from Aligned to With significant gaps. A missing core control caps the band however good the rest looks.' },
    ],
    related: [
      { to: 'vendor-assessments', label: 'Next: track the answers' },
      { to: 'trust-centre', label: 'Answer questionnaires about yourself' },
    ],
    routes: ['/vendors', '/vendors/*'],
  },
  {
    slug: 'vendor-assessments',
    title: 'Vendor assessments',
    group: 'thirdparty',
    summary: 'Who you have asked, how far they got, and where they stand.',
    purpose:
      'Every questionnaire you send to a supplier shows up here: sent, in progress or answered, with the ' +
      'resulting posture. It is the tracking board for your third-party due diligence.',
    related: [
      { to: 'vendors', label: 'Back to the vendor register' },
    ],
    routes: ['/vendors/assessments'],
  },
  {
    slug: 'trust-centre',
    title: 'Trust Centre',
    group: 'thirdparty',
    summary: 'Answer the questions customers keep asking once, and share the answers on your terms.',
    purpose:
      'The Trust Centre turns the questionnaire burden around. Answer the common security questions one ' +
      'time, tag each answer by who may see it, and give a client, partner or auditor a scoped, read-only ' +
      'login instead of filling in their spreadsheet.',
    steps: [
      { title: 'Build your answers', body: 'Record your replies once. Pull from what you have already answered elsewhere rather than starting cold.' },
      { title: 'Tag each answer', body: 'Mark it public, under NDA, or for a named customer only. The tag decides who can ever see it.' },
      { title: 'Grant scoped access', body: 'Invite an external user as a Client, Partner or Auditor. Each preset opens only the right tier of answers, and access is time-limited and yours to revoke.' },
    ],
    tips: [
      'External access is deliberately isolated from your own accounts — a trust-portal login can never reach the rest of OpenComply.',
      'Configure the presets and the public page from Trust Centre settings.',
    ],
    related: [
      { to: 'reports', label: 'Hand over a report instead' },
    ],
    routes: ['/trust-centre'],
  },

  // --- EU product rules ----------------------------------------------------
  {
    slug: 'cra-conformity',
    title: 'CRA conformity',
    group: 'eu',
    summary: 'Get a product ready for the Cyber Resilience Act and generate its Declaration of Conformity.',
    purpose:
      'For products with digital elements placed on the EU market, the Cyber Resilience Act requires ' +
      'conformity. Register each product with its criticality class, work the CE-readiness checklist, handle ' +
      'vulnerabilities against the ENISA clocks, and generate the EU Declaration of Conformity to hand over.',
    steps: [
      { title: 'Register the product', body: 'Add it with its criticality class, which sets how its conformity must be assessed.' },
      { title: 'Work the CE checklist', body: 'The checklist is drawn from your CRA assessment — close it item by item.' },
      { title: 'Handle vulnerabilities', body: 'Record vulnerabilities and track the ENISA reporting clocks for each.' },
      { title: 'Generate the Declaration', body: 'Produce the EU Declaration of Conformity once the product is ready.' },
    ],
    related: [
      { to: 'assessments', label: 'Complete the CRA assessment first' },
    ],
    routes: ['/cra-conformity/*', '/cra-conformity'],
  },

  // --- Show your standing --------------------------------------------------
  {
    slug: 'reports',
    title: 'Reports and the board report',
    group: 'report',
    summary: 'Generate the documents you hand over — including a branded board report in one click.',
    purpose:
      'Reports is where you produce the artefacts other people read: the Statement of Applicability, ' +
      'evidence exports, and the board report — a short, branded PDF of where you stand, what needs ' +
      'attention, your third-party risk and what changed in the last 30 days.',
    steps: [
      { title: 'Pick the report', body: 'Choose the board report for an executive summary, or an export for an auditor.' },
      { title: 'Generate and hand over', body: 'The board report is a branded PDF, logo and all — ready to attach to an email.' },
    ],
    related: [
      { to: '/insights', label: 'See the numbers behind it' },
      { to: 'soa', label: 'Statement of Applicability' },
    ],
    routes: ['/reports'],
  },
  {
    slug: 'insights',
    title: 'Insights',
    group: 'report',
    summary: 'An executive scorecard and your compliance trend over time.',
    purpose:
      'Insights is the numbers view: a scorecard of your headline figures and a chart of your compliance ' +
      'over time, captured automatically each day, so you can show the line going up.',
    related: [
      { to: '/', label: 'Back to the dashboard' },
      { to: 'reports', label: 'Put it in the board report' },
    ],
    routes: ['/insights'],
  },

  // --- Administration ------------------------------------------------------
  {
    slug: 'scans',
    title: 'Scans',
    group: 'admin',
    summary: 'Automated checks that bring outside evidence into your compliance picture.',
    purpose:
      'Scans gather evidence automatically rather than by hand. Run them to pull in signals that feed your ' +
      'controls and your vendor picture.',
    related: [
      { to: 'vendors', label: 'Vendors' },
      { to: '/credits', label: 'Credits' },
    ],
    routes: ['/scans'],
  },
  {
    slug: 'audit',
    title: 'Audit trail',
    group: 'admin',
    summary: 'A tamper-evident record of who did what, for when someone has to ask.',
    purpose:
      'The audit trail logs the actions taken in your organisation. It is there for accountability — and ' +
      'for the day an auditor or an investigation needs to see the history.',
    routes: ['/audit'],
  },
  {
    slug: 'content-watch',
    title: 'Regulatory watch',
    group: 'admin',
    summary: 'Keep an eye on changes to the frameworks and content behind your assessments.',
    purpose:
      'Regulations and control sets change. The regulatory watch surfaces updates to the underlying content ' +
      'so your assessments stay current rather than drifting out of date.',
    related: [
      { to: 'catalogue', label: 'Control catalogue' },
    ],
    routes: ['/content-watch'],
  },
  {
    slug: 'translations',
    title: 'Translations',
    group: 'admin',
    summary: 'Manage the translated content that lets the app speak your language.',
    purpose:
      'This is where the translated content is managed — the layer that lets assessment questions and other ' +
      'content appear in Greek and the other supported languages, not just the menus.',
    related: [
      { to: 'assessments', label: 'See translated assessments' },
    ],
    routes: ['/translations'],
  },
  {
    slug: 'organizations',
    title: 'Organisations',
    group: 'admin',
    summary: 'The organisations in your account and how they are set up.',
    purpose:
      'If you manage more than one organisation — subsidiaries, clients, business units — this is where they ' +
      'are created and configured.',
    related: [
      { to: 'users', label: 'Users' },
      { to: '/credits', label: 'Credits' },
    ],
    routes: ['/organizations'],
  },
  {
    slug: 'users',
    title: 'Users and roles',
    group: 'admin',
    summary: 'Invite people and give them the access their job needs — no more.',
    purpose:
      'Add the people who work in OpenComply and set each one’s role. Roles are capability-based: a ' +
      'person sees and can do only what their role allows, which is both safer and less cluttered for them.',
    related: [
      { to: '/security', label: 'Your own account and sign-in' },
    ],
    routes: ['/users'],
  },
  {
    slug: 'credits',
    title: 'Credits',
    group: 'admin',
    summary: 'What licensing frameworks and running scans draws on, and what you have left.',
    purpose:
      'Some actions — licensing a framework, running certain scans — draw on credits. This screen shows your ' +
      'balance and what has been spent.',
    related: [
      { to: 'frameworks', label: 'Frameworks' },
    ],
    routes: ['/credits'],
  },
  {
    slug: 'news',
    title: 'News',
    group: 'admin',
    summary: 'What is new in OpenComply, release by release.',
    purpose:
      'The News screen is the changelog in plain language: every release, what it added, improved or fixed, ' +
      'and why it matters to you.',
    routes: ['/news'],
  },
  {
    slug: 'security',
    title: 'Your account and sign-in',
    group: 'admin',
    summary: 'Your password, two-factor sign-in and personal settings.',
    purpose:
      'Manage your own account here: change your password, set up two-factor authentication, and adjust your ' +
      'personal settings.',
    routes: ['/security'],
  },
];

// ---------------------------------------------------------------------------
//  The ordered "getting started" path
// ---------------------------------------------------------------------------

export interface ManualPathStep {
  n: number;
  title: string;
  blurb: string;
  /** The article that explains this step in full. */
  slug: string;
}

/**
 * The recommended order for a new organisation. The numbers are the path a
 * person follows end to end; each step points at the article that covers it.
 */
export const GETTING_STARTED_PATH: ManualPathStep[] = [
  { n: 1, title: 'Set your goal', blurb: 'Tell OpenComply what you are working towards so it can guide you.', slug: 'getting-started' },
  { n: 2, title: 'Licence a framework', blurb: 'Open the regulation you must meet — this unlocks its questionnaire.', slug: 'frameworks' },
  { n: 3, title: 'Answer the assessment', blurb: 'Work through the questions and finalise to find where you fall short.', slug: 'assessments' },
  { n: 4, title: 'Work your gaps', blurb: 'Turn each shortfall into a remediation plan with an owner and a date.', slug: 'gaps' },
  { n: 5, title: 'Keep a risk register', blurb: 'Record and treat your information-security risks — auditors expect it.', slug: 'risk-register' },
  { n: 6, title: 'Write the policies', blurb: 'Back your controls with the written policies they require.', slug: 'policies' },
  { n: 7, title: 'Reuse with the catalogue', blurb: 'Prove a control once and let it count across every framework.', slug: 'catalogue' },
  { n: 8, title: 'Assess your suppliers', blurb: 'Send vendors a questionnaire and track their posture.', slug: 'vendors' },
  { n: 9, title: 'Stay on top of dates', blurb: 'Let the calendar and incidents keep the deadlines in front of you.', slug: 'calendar' },
  { n: 10, title: 'Show your standing', blurb: 'Generate the board report and watch the trend in Insights.', slug: 'reports' },
];

// ---------------------------------------------------------------------------
//  Lookups
// ---------------------------------------------------------------------------

const BY_SLUG = new Map(MANUAL_ARTICLES.map((a) => [a.slug, a]));

export function articleBySlug(slug: string): ManualArticle | undefined {
  return BY_SLUG.get(slug);
}

export function articlesInGroup(groupId: string): ManualArticle[] {
  return MANUAL_ARTICLES.filter((a) => a.group === groupId);
}

/** A `related.to` is a route if it starts with "/"; otherwise an article slug. */
export function isRouteLink(to: string): boolean {
  return to.startsWith('/');
}

/**
 * How well a pattern matches a path, or 0 for no match. A higher score is a
 * more specific match: an exact pattern beats a wildcard, and among the same
 * kind a longer pattern beats a shorter one. So "/policies/templates" (exact)
 * wins over "/policies/*", and "/vendors/assessments" over "/vendors/*".
 */
function matchScore(pattern: string, path: string): number {
  if (pattern.endsWith('/*')) {
    const base = pattern.slice(0, -2);
    if (path === base || path.startsWith(base + '/')) return base.length + 1;
    return 0;
  }
  // Exact match: rank it above any wildcard by a large constant, then by length.
  return pattern === path ? 1000 + pattern.length : 0;
}

/**
 * The article that documents a given app route, or undefined. Scans every
 * article's routes and returns the slug of the most specific match — so
 * "/assessments/:id" resolves to the assessment guidance, "/policies/templates"
 * to the templates article rather than the policies one, and an unmapped screen
 * to undefined (the "?" then opens the manual's contents).
 */
export function manualSlugForRoute(path: string): string | undefined {
  let best: { slug: string; score: number } | undefined;
  for (const article of MANUAL_ARTICLES) {
    for (const pattern of article.routes) {
      const score = matchScore(pattern, path);
      if (score > 0 && (!best || score > best.score)) {
        best = { slug: article.slug, score };
      }
    }
  }
  return best?.slug;
}
